// Reduced-motion plumbing (plan-gen1.md hard constraint #3). The canvas
// render loop can't read a CSS media query implicitly, so GameCanvas reads
// `window.matchMedia('(prefers-reduced-motion: reduce)')` on mount + on
// 'change' and threads the resulting boolean into the render calls below as
// a plain value -- keeping the actual freeze logic here as PURE functions
// so it's testable without mounting a canvas.

/** Reads the live media-query match. Safe to call in non-browser test
 * environments that lack matchMedia (returns false, i.e. "motion allowed"). */
export function readPrefersReducedMotion(win: (Window & typeof globalThis) | undefined = typeof window !== 'undefined' ? window : undefined): boolean {
  if (!win || typeof win.matchMedia !== 'function') return false;
  return win.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** Freezes any time-driven DECORATIVE animation phase (glow pulse, fright
 * flash blink) when the user prefers reduced motion, by substituting a fixed
 * reference instant for the live clock -- every sin()/blink-modulo call that
 * receives this frozen value stops advancing frame to frame, with no need to
 * special-case each animation site individually.
 *
 * Must NOT be used for gameplay interpolation (pac/ghost position lerp
 * fraction) -- that represents real, already-happened server motion, not
 * decorative animation, and freezing it would make entities stop moving. */
export function effectiveNowMs(rawNowMs: number, reduced: boolean): number {
  return reduced ? 0 : rawNowMs;
}

// PART 4: manual override for the above OS-only signal. `settings.ts`
// stores this as `reducedMotion: 'auto' | 'on' | 'off'` -- 'auto' is the
// pre-existing behavior (follow `prefers-reduced-motion` exactly), 'on'
// forces the reduced/frozen path even when the OS itself reports
// no-preference, 'off' forces full motion even when the OS prefers
// reduced (an explicit user choice always wins over the OS default).
export type ReducedMotionSetting = 'auto' | 'on' | 'off';

/** Pure resolver: combines the user's `Settings.reducedMotion` choice with
 * the live OS media-query result into the one effective boolean every
 * consumer (GameCanvas's decorative clock, the CSS `.reduced-motion` class
 * toggle in App.tsx) actually acts on. Exported so the full auto/on/off x
 * media-query matrix is unit-testable without a browser. */
export function resolveEffectiveReducedMotion(setting: ReducedMotionSetting, osPrefersReduced: boolean): boolean {
  if (setting === 'on') return true;
  if (setting === 'off') return false;
  return osPrefersReduced;
}
